import React from "react";
import { useNavigate } from "react-router-dom";
import { useNavigationContext } from "../../common/navigation/NavigationContext";
// 🎯 Alias bypasses the underscore naming rules for react/jsx-pascal-case
import AttestationSigner from "./SEC_AttestationSigner";

export default function SEC_RepresentativeView({
  step,
  error,
  submitting,
  fullName,
  setFullName,
  dateOfBirth,
  setDateOfBirth,
  onSaveProfile,
  newCompany,
  setNewCompany,
  onPrimaryFieldChange,
  onCompanyFieldChange,
  validCountries,
  registryStatus,
  registryResults,
  registryError,
  selectedMatchId,
  confirmedCompany,
  onCheckRegistration,
  onSelectMatch,
  onConfirmSelection,
  onRejectRegistryMatches,
  onDiscardConfirmedMatch,
  onCreateAndRepresent,
  corporateEntityId,
  companyName,
  onAttestationComplete,
  backPath = "/registered/dashboard",
}) {
  const navigate = useNavigate();
  const { activeWorkflow } = useNavigationContext();
  if (step === "LOADING") {
    return (
      <div className="text-center p-5">
        <div className="spinner-border text-primary" role="status" />
      </div>
    );
  }

  return (
    <div className="page-main mt-5 pt-5">
      <div className="page-main-inner container">
        <div className="row justify-content-center">
          <div className="col-xl-7">
            <div className="mb-4">
              <h2 className="h4 fw-bold mb-1">Register Company</h2>
              <p className="text-muted extra-small text-uppercase fw-bold">
                Become a Company Representative
              </p>
            </div>

            {error && <div className="alert alert-danger py-2 small">{error}</div>}

            {step === "PROFILE" && (
              <div className="card shadow-sm border-0 p-4">
                <h6 className="fw-bold text-uppercase extra-small text-muted mb-3 text-primary">
                  Your Details
                </h6>
                <div className="row g-2">
                  <div className="col-md-7">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Full Name *"
                      value={fullName}
                      onChange={(e) => setFullName(e.target.value)}
                    />
                  </div>
                  <div className="col-md-5">
                    <input
                      type="date"
                      className="form-control form-control-sm"
                      placeholder="Date of Birth *"
                      value={dateOfBirth}
                      onChange={(e) => setDateOfBirth(e.target.value)}
                    />
                  </div>
                </div>
                <button
                  className="btn btn-sm btn-dark mt-3 fw-bold extra-small px-4"
                  disabled={submitting}
                  onClick={onSaveProfile}
                >
                  {submitting ? "SAVING..." : "CONTINUE"}
                </button>
              </div>
            )}

            {step === "COMPANY" && (
              <div className="card shadow-sm border-0 p-4">
                <h6 className="fw-bold text-uppercase extra-small text-muted mb-3 text-primary">
                  Register Your Company
                </h6>
                <div className="row g-2">
                  <div className="col-md-3">
                    <select
                      className="form-select form-control-sm"
                      value={newCompany.country}
                      disabled={registryStatus === "confirmed"}
                      onChange={(e) => onPrimaryFieldChange("country", e.target.value)}
                    >
                      <option value="">Country *</option>
                      {validCountries.map((c) => (
                        <option key={c} value={c}>{c}</option>
                      ))}
                    </select>
                  </div>
                  <div className="col-md-5">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Company Name"
                      value={newCompany.name}
                      disabled={registryStatus === "confirmed"}
                      onChange={(e) => onCompanyFieldChange("name", e.target.value)}
                    />
                  </div>
                  <div className="col-md-4">
                    <input
                      type="text"
                      className="form-control form-control-sm"
                      placeholder="Company Reg Number"
                      value={newCompany.regNumber}
                      disabled={registryStatus === "confirmed"}
                      onChange={(e) => onCompanyFieldChange("regNumber", e.target.value)}
                    />
                  </div>
                </div>

                {/* Enter a Name and/or Reg Number + Country, then check against the
                    official registry (Companies House / DINUM / Zefix). A confirmed
                    match populates the address directly - no separate Dogma
                    validation needed for gov-sourced data - and locks this whole
                    section (search inputs disabled above) until discarded. No
                    match / rejected / unsupported country falls straight through
                    to manual entry below. */}
                <div className="mt-2">
                  {registryStatus !== "confirmed" && (
                    <button
                      type="button"
                      className="btn btn-sm btn-outline-primary fw-bold extra-small px-3"
                      disabled={registryStatus === "checking"}
                      onClick={onCheckRegistration}
                    >
                      {registryStatus === "checking" ? "CHECKING..." : "CHECK REGISTRATION"}
                    </button>
                  )}

                  {registryError && (
                    <div className="alert alert-warning py-1 px-2 small mt-2 mb-0">{registryError}</div>
                  )}

                  {registryStatus === "confirmed" && confirmedCompany && (
                    <div className="alert alert-success py-2 px-3 small mt-2 mb-0">
                      <div className="d-flex justify-content-between align-items-start">
                        <div>
                          <div className="fw-bold mb-1">
                            <i className="bi bi-check-circle-fill me-1"></i>
                            {confirmedCompany.companyName}
                          </div>
                          <address className="mb-0 extra-small lh-lg">
                            {confirmedCompany.addressLine1}<br />
                            {confirmedCompany.addressLine2 && <>{confirmedCompany.addressLine2}<br /></>}
                            {confirmedCompany.town && <>{confirmedCompany.town}<br /></>}
                            {confirmedCompany.county && <>{confirmedCompany.county}<br /></>}
                            {confirmedCompany.postcode}<br />
                            {newCompany.country}
                          </address>
                        </div>
                        <button
                          type="button"
                          className="btn btn-sm btn-outline-secondary extra-small px-2"
                          onClick={onDiscardConfirmedMatch}
                        >
                          Discard
                        </button>
                      </div>
                    </div>
                  )}

                  {registryStatus === "rejected" && (
                    <div className="alert alert-secondary py-1 px-2 small mt-2 mb-0">
                      No registry match confirmed - enter the address manually below.
                    </div>
                  )}

                  {registryStatus === "results" && (
                    <div className="mt-2">
                      <p className="extra-small text-muted fw-bold text-uppercase mb-2">
                        {registryResults.length === 1 ? "Is this your company?" : "Select your company"}
                      </p>
                      <div className="table-responsive">
                        <table className="table table-sm table-striped table-bordered align-middle mb-2">
                          <thead>
                            <tr>
                              <th className="extra-small text-muted" style={{ width: "2.5rem" }}></th>
                              <th className="extra-small text-muted text-uppercase">Company Name</th>
                              <th className="extra-small text-muted text-uppercase">ID</th>
                              <th className="extra-small text-muted text-uppercase">Address</th>
                              <th className="extra-small text-muted text-uppercase">Status</th>
                            </tr>
                          </thead>
                          <tbody>
                            {registryResults.map((match) => (
                              <tr
                                key={match.companyId}
                                onClick={() => onSelectMatch(match.companyId)}
                                style={{ cursor: "pointer" }}
                              >
                                <td>
                                  <input
                                    type="checkbox"
                                    className="form-check-input"
                                    checked={String(selectedMatchId) === String(match.companyId)}
                                    onChange={() => onSelectMatch(match.companyId)}
                                  />
                                </td>
                                <td className="small fw-bold">{match.companyName}</td>
                                <td className="small">{match.companyId}</td>
                                <td className="small">{match.addressSnippet || "—"}</td>
                                <td className="small">{match.status || "—"}</td>
                              </tr>
                            ))}
                          </tbody>
                        </table>
                      </div>
                      <button
                        type="button"
                        className="btn btn-sm btn-dark fw-bold extra-small px-3"
                        disabled={!selectedMatchId}
                        onClick={onConfirmSelection}
                      >
                        CONFIRM SELECTION
                      </button>
                      <button
                        type="button"
                        className="btn btn-sm btn-link text-muted extra-small ms-2"
                        onClick={onRejectRegistryMatches}
                      >
                        None of these — enter manually
                      </button>
                    </div>
                  )}
                </div>

                {registryStatus !== "confirmed" && (
                  <>
                    <h6 className="fw-bold text-uppercase extra-small text-muted mt-4 mb-3 text-primary">
                      Registered Address
                    </h6>

                    {/* Country was already picked above (it's also the registry-check
                        input). Stacked one field per line - not a wide multi-column
                        row. Address Line 1 + Postcode are compulsory - they're the
                        key Create & Represent uses to quietly validate the address
                        (Dogma) before creating anything, when no registry match was
                        confirmed. Line 2/Town/County are optional and only need
                        filling by hand if that quiet lookup can't find a match either. */}
                    <div className="mb-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Address Line 1 *"
                        value={newCompany.addressLine1}
                        onChange={(e) => onPrimaryFieldChange("addressLine1", e.target.value)}
                      />
                    </div>
                    <div className="mb-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Address Line 2"
                        value={newCompany.addressLine2}
                        onChange={(e) => setNewCompany((p) => ({ ...p, addressLine2: e.target.value }))}
                      />
                    </div>
                    <div className="mb-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Town"
                        value={newCompany.town}
                        onChange={(e) => setNewCompany((p) => ({ ...p, town: e.target.value }))}
                      />
                    </div>
                    <div className="mb-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="County"
                        value={newCompany.county}
                        onChange={(e) => setNewCompany((p) => ({ ...p, county: e.target.value }))}
                      />
                    </div>
                    <div className="mb-2">
                      <input
                        type="text"
                        className="form-control form-control-sm"
                        placeholder="Postcode *"
                        value={newCompany.postcode}
                        onChange={(e) => onPrimaryFieldChange("postcode", e.target.value)}
                      />
                    </div>

                    {(newCompany.addressLine1 || newCompany.postcode) && (
                      <address className="mb-0 small lh-lg mt-3 mb-0 text-muted border-start ps-3">
                        {newCompany.addressLine1}<br />
                        {newCompany.addressLine2 && <>{newCompany.addressLine2}<br /></>}
                        {newCompany.town && <>{newCompany.town}<br /></>}
                        {newCompany.county && <>{newCompany.county}<br /></>}
                        {newCompany.postcode}<br />
                        {newCompany.country}
                      </address>
                    )}
                  </>
                )}

                <button
                  className="btn btn-sm btn-dark mt-3 fw-bold extra-small px-4"
                  disabled={submitting}
                  onClick={onCreateAndRepresent}
                >
                  {submitting ? "REGISTERING..." : "CREATE & REPRESENT"}
                </button>
                <p className="text-muted extra-small mt-3 mb-0">
                  Already represented by someone else? Ask them to invite you — joining an
                  existing company isn't self-service yet.
                </p>
              </div>
            )}

            {step === "ATTESTATION" && (
              <>
                <p className="small text-muted mb-3">
                  Representing <strong>{companyName}</strong>
                </p>
                <AttestationSigner
                  corporateEntityId={corporateEntityId}
                  onComplete={onAttestationComplete}
                />
              </>
            )}

            {step === "DONE" && (
              <div className="card shadow-sm border-0 p-5 text-center">
                <i className="bi bi-check-circle-fill text-success h1 d-block mb-3"></i>
                <h5 className="fw-bold mb-2">You represent {companyName}</h5>
                <p className="text-muted small mb-0">
                  You can now manage this company from Corporate Details.
                </p>
              </div>
            )}

            {/* 5. FOOTER NAVIGATION */}
            <div className="mt-5 pt-4 border-top d-flex justify-content-between align-items-center">
              <button className="btn btn-link text-decoration-none p-0 text-primary fw-bold small" onClick={() => navigate(backPath)}>
                <i className="bi bi-arrow-left me-2"></i> Back
              </button>
              <div className="text-end">
                <span className="text-muted extra-small d-block">Workflow Context</span>
                <span className="fw-bold small">{activeWorkflow?.name || "Register Company"}</span>
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
